import { Flex, IconButton, Tooltip } from '@chakra-ui/react';
import type HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { BIZY_KIT_I18N_NAMESPACE } from '../i18n';
import { exportFilename } from './export-filename';
import { CheckOutline, DocumentDownloadOutline, DuplicateOutline, PhotographOutline } from './icons';

// Matches the table action bar so chart, matrix, and table exports look identical.
const ACTION_STYLES = {
	size: 'xs' as const,
	variant: 'ghost' as const,
	colorScheme: 'secondary' as const,
	color: 'info.icon',
	borderRadius: '8px',
	_hover: { bg: 'secondary.bgAlpha', color: 'secondary.fgActive' },
	_active: { bg: 'secondary.bgAlphaHover', color: 'secondary.fgActive' },
};

interface HighchartsExportBarProps {
	chartRef: RefObject<HighchartsReact.RefObject>;
	title?: string;
	xLabel?: string;
	yLabel?: string;
	subtitle?: string;
	fallback: string;
	variant: string;
	idPrefix: string;
}

/**
 * Shared Copy / CSV / PNG export action bar for Highcharts-backed nodes (chart,
 * matrix). Copy + CSV serialize the series data; PNG rasterizes client-side.
 */
export function HighchartsExportBar({ chartRef, title, xLabel, yLabel, subtitle, fallback, variant, idPrefix }: HighchartsExportBarProps) {
	const { t } = useTranslation(BIZY_KIT_I18N_NAMESPACE);
	const { track, conversationId, messageId } = useBizyAnalytics();
	const filename = exportFilename(title, fallback);
	const [copied, setCopied] = useState(false);
	const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		return () => {
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
		};
	}, []);

	const handleCopy = async () => {
		const chart = chartRef.current?.chart;
		if (!chart) return;
		try {
			await navigator.clipboard.writeText(chart.getCSV());
			setCopied(true);
			if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
			copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
			track(BizyAnalyticsEvent.ChartExported, { format: 'copy', variant, conversationId, messageId, path: window.location.pathname });
		} catch {
			// Clipboard write failed silently
		}
	};

	const handleDownloadCsv = () => {
		const chart = chartRef.current?.chart;
		if (!chart) return;
		chart.getFilename = () => filename;
		chart.downloadCSV();
		track(BizyAnalyticsEvent.ChartExported, { format: 'csv', variant, conversationId, messageId, path: window.location.pathname });
	};

	const handleDownloadPng = () => {
		const chart = chartRef.current?.chart;
		if (!chart) return;
		chart.exportChartLocal(
			{ type: 'image/png', filename },
			{
				...(title ? { title: { text: title } } : {}),
				...(subtitle ? { subtitle: { text: subtitle } } : {}),
				xAxis: { labels: { useHTML: false }, ...(xLabel ? { title: { text: xLabel } } : {}) },
				yAxis: { labels: { useHTML: false }, ...(yLabel ? { title: { text: yLabel } } : {}) },
				legend: { useHTML: false },
			}
		);
		track(BizyAnalyticsEvent.ChartExported, { format: 'png', variant, conversationId, messageId, path: window.location.pathname });
	};

	return (
		<Flex className={`${idPrefix}-actions`} gap="4px" opacity={0} transition="opacity 0.15s ease-in-out">
			<Tooltip label={copied ? t('feedback.copied') : t('feedback.copy')} placement="top">
				<IconButton
					data-testid={`${idPrefix}-copy`}
					aria-label={t('feedback.copy')}
					icon={copied ? <CheckOutline boxSize={5} /> : <DuplicateOutline boxSize={5} />}
					{...ACTION_STYLES}
					onClick={handleCopy}
				/>
			</Tooltip>
			<Tooltip label={t('feedback.downloadCsv')} placement="top">
				<IconButton
					data-testid={`${idPrefix}-download-csv`}
					aria-label={t('feedback.downloadCsv')}
					icon={<DocumentDownloadOutline boxSize={5} />}
					{...ACTION_STYLES}
					onClick={handleDownloadCsv}
				/>
			</Tooltip>
			<Tooltip label={t('feedback.downloadImage')} placement="top">
				<IconButton
					data-testid={`${idPrefix}-download-png`}
					aria-label={t('feedback.downloadImage')}
					icon={<PhotographOutline boxSize={5} />}
					{...ACTION_STYLES}
					onClick={handleDownloadPng}
				/>
			</Tooltip>
		</Flex>
	);
}
